/// <reference types="google.maps" />
import { useCallback, useEffect, useRef, useState } from 'react';

// Local aliases for the long `google.maps.*` paths used throughout this file.
type Place = google.maps.places.Place;
type PlacePrediction = google.maps.places.PlacePrediction;
type AutocompleteRequest = google.maps.places.AutocompleteRequest;
type AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken;
type AutocompleteSuggestion = google.maps.places.AutocompleteSuggestion;
type PlacesLibrary = google.maps.PlacesLibrary;

/**
 * A single autocomplete suggestion returned by the Places Autocomplete API.
 */
export type AddressPrediction = {
  placeId: string;
  /** Full formatted prediction text, e.g. "123 Main St, Los Angeles, CA, USA". */
  text: string;
  /** Bolded portion of the prediction (the part matching the user's input). */
  mainText?: string;
  /** Secondary disambiguating portion, e.g. "Los Angeles, CA, USA". */
  secondaryText?: string;
  /** Raw SDK object — passed internally to `selectPrediction` to fetch place details. */
  sdkPrediction: PlacePrediction;
};

type Options = {
  /** ISO 3166-1 alpha-2 country code to restrict results, e.g. `'us'`. */
  country?: string;
  /** Place types passed as `includedPrimaryTypes`, e.g. `['geocode', 'establishment']`. */
  types?: string[];
  debounceMs?: number;
};

/**
 * A watchdog `timeout` (cleared if the import resolves late) vs a genuine
 * `importLibrary` `rejected` failure, so callers can log them apart. See CN-50617.
 */
export type PlacesLoadError =
  | { reason: 'timeout' }
  | { reason: 'rejected'; error: unknown };

/**
 * Return value of `useAddressAutocomplete`.
 * `selectPrediction` resolves with the raw `Place` — callers
 * are responsible for mapping it to their own data shape.
 */
export type UseAddressAutocompleteResult = {
  ready: boolean;
  /** The reason Places failed to load, or `null` while loading/loaded; `ready` stays false when set. */
  loadError: PlacesLoadError | null;
  value: string;
  setValue: (next: string, shouldFetch?: boolean) => void;
  predictions: AddressPrediction[];
  clearPredictions: () => void;
  selectPrediction: (prediction: AddressPrediction) => Promise<Place>;
};

const DEFAULT_DEBOUNCE_MS = 300;

/** Watchdog for a hung Places load (blocked script, stalled network). */
const PLACES_LOAD_TIMEOUT_MS = 5000;

/**
 * Allowlist of Place Details fields that stay within Google's **Essentials** SKU
 * tier ($5 per 1,000 calls). Adding any Pro-tier field (e.g. `regularOpeningHours`)
 * silently flips the entire call to Pro pricing ($17 per 1,000) — Google does not
 * warn or surface this.
 *
 * Tier reference: [Place Data Fields](https://developers.google.com/maps/documentation/places/web-service/place-data-fields)
 */
type EssentialsField =
  | 'addressComponents'
  | 'adrFormatAddress'
  | 'formattedAddress'
  | 'location'
  | 'viewport'
  | 'googleMapsUri'
  | 'plusCode'
  | 'types'
  | 'displayName'
  | 'id'
  | 'utcOffsetMinutes'
  | 'attributions';

/** Place Details fields requested via `fetchFields`. Constrained to {@link EssentialsField} to prevent silent SKU-tier upgrades. */
const PLACE_FIELDS: EssentialsField[] = [
  'addressComponents',
  'formattedAddress',
  'location',
  'id',
  'types',
];

function buildAutocompleteRequest(
  input: string,
  sessionToken: AutocompleteSessionToken,
  country?: string,
  types?: string[]
): AutocompleteRequest {
  const request: AutocompleteRequest = {
    input,
    sessionToken,
  };
  if (country) request.includedRegionCodes = [country];
  if (types && types.length) request.includedPrimaryTypes = types;
  return request;
}

function suggestionToPrediction(
  s: AutocompleteSuggestion
): AddressPrediction | null {
  const p = s.placePrediction;
  if (!p) return null;
  return {
    placeId: p.placeId,
    text: p.text?.text || '',
    mainText: p.mainText?.text,
    secondaryText: p.secondaryText?.text,
    sdkPrediction: p,
  };
}

/**
 * Manages Google Places Autocomplete state: debounced suggestion fetching,
 * session token lifecycle, and place detail retrieval. Loads the Places
 * library on mount via Dynamic Library Import.
 */
export function useAddressAutocomplete(
  options: Options = {}
): UseAddressAutocompleteResult {
  const { country, types, debounceMs = DEFAULT_DEBOUNCE_MS } = options;

  const [value, setValueState] = useState('');
  const [predictions, setPredictions] = useState<AddressPrediction[]>([]);
  const [placesReady, setPlacesReady] = useState(false);
  const [loadError, setLoadError] = useState<PlacesLoadError | null>(null);

  const placesLibRef = useRef<PlacesLibrary | null>(null);
  const sessionTokenRef = useRef<AutocompleteSessionToken | null>(null);
  const skipNextFetchRef = useRef(false);

  // String key avoids array identity re-triggering the effect on each render.
  const typesKey = types ? types.join(',') : '';

  useEffect(() => {
    let cancelled = false;
    // A blocked or hung Places load otherwise leaves `ready` false forever with
    // no signal. Flag it so the consumer can log/surface the failure — this
    // restores the load watchdog the dynamic-import migration dropped.
    const timeout = window.setTimeout(() => {
      if (!cancelled) setLoadError({ reason: 'timeout' });
    }, PLACES_LOAD_TIMEOUT_MS);
    void (async () => {
      try {
        const placesLib = await google.maps.importLibrary('places');
        if (cancelled) return;
        placesLibRef.current = placesLib;
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
        window.clearTimeout(timeout);
        // Clear any watchdog-tripped error: a late resolve means Places did
        // load, so the final state should be ready, not errored.
        setLoadError(null);
        setPlacesReady(true);
      } catch (error) {
        if (cancelled) return;
        window.clearTimeout(timeout);
        setLoadError({ reason: 'rejected', error });
      }
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (!placesLibRef.current || !sessionTokenRef.current) {
      return undefined;
    }

    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return undefined;
    }

    if (!value) {
      setPredictions([]);
      return undefined;
    }

    let cancelled = false;

    async function run() {
      try {
        const request = buildAutocompleteRequest(
          value,
          sessionTokenRef.current!,
          country,
          types
        );
        const { suggestions } =
          await placesLibRef.current!.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request
          );
        if (cancelled) return;

        const next: AddressPrediction[] = (suggestions || [])
          .map(suggestionToPrediction)
          .filter(
            (p: AddressPrediction | null): p is AddressPrediction => p !== null
          );

        setPredictions(next);
      } catch {
        if (!cancelled) setPredictions([]);
      }
    }

    const handle = window.setTimeout(() => {
      void run();
    }, debounceMs);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [value, country, typesKey, debounceMs]);

  const setValue = useCallback((next: string, shouldFetch = true) => {
    if (!shouldFetch) skipNextFetchRef.current = true;
    setValueState(next);
  }, []);

  const clearPredictions = useCallback(() => {
    setPredictions([]);
  }, []);

  const selectPrediction = useCallback(
    async (prediction: AddressPrediction): Promise<Place> => {
      const place = prediction.sdkPrediction.toPlace();
      await place.fetchFields({ fields: PLACE_FIELDS });

      // Rotate session token so the next typing session is billed separately.
      if (placesLibRef.current) {
        sessionTokenRef.current =
          new placesLibRef.current.AutocompleteSessionToken();
      }

      return place;
    },
    []
  );

  return {
    ready: placesReady,
    loadError,
    value,
    setValue,
    predictions,
    clearPredictions,
    selectPrediction,
  };
}
